import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Calendar, Loader2, Printer, ShieldCheck, ShieldX, Syringe } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Separator } from "@akuna/ui";
import { supabase } from "@/integrations/supabase/client";
import { formatDateBR } from "@/lib/date-utils";

type VerifyResult = "idle" | "checking" | "found" | "not_found" | "error";

interface VaccinationEntry {
  id: string;
  vaccine_name: string | null;
  application_date: string;
  dose_number: number;
  batch_number: string | null;
  expiry_date: string | null;
  next_dose_date: string | null;
  next_dose_completed_at?: string | null;
  notes: string | null;
  adverse_reaction: string | null;
  vet_name: string | null;
  vet_crmv: string | null;
  clinic_name: string | null;
}

interface VaccinationData {
  pet_id: string;
  pet_name: string;
  pet_birth_date: string | null;
  species: string | null;
  breed: string | null;
  owner_name: string | null;
  vaccinations: VaccinationEntry[];
  total: number;
  generated_at: string;
}

export default function VerifyVaccination() {
  const { t } = useTranslation("patients");
  const [searchParams] = useSearchParams();
  const initialPetId = useMemo(() => searchParams.get("pet_id") ?? "", [searchParams]);

  const [petId, setPetId] = useState(initialPetId);
  const [result, setResult] = useState<VerifyResult>("idle");
  const [data, setData] = useState<VaccinationData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleVerify = async () => {
    setErrorMessage(null);
    setData(null);

    if (!petId.trim()) {
      setResult("error");
      setErrorMessage(t("vaccineVerify.idRequired", { defaultValue: "Informe o ID do paciente." }));
      return;
    }

    try {
      setResult("checking");

      const { data: response, error } = await supabase.functions.invoke("vaccination-verify", {
        body: { pet_id: petId.trim() },
      });

      if (error) throw new Error(error.message);

      const payload = response as { error?: string } & VaccinationData;
      if (payload?.error === "pet_not_found") {
        setResult("not_found");
        setErrorMessage(t("vaccineVerify.notFound", { defaultValue: "Paciente não encontrado." }));
        return;
      }
      if (payload?.error) throw new Error(payload.error);

      setData(payload);
      setResult("found");
    } catch (err) {
      console.error("Error verifying vaccinations", err);
      setResult("error");
      setErrorMessage(t("vaccineVerify.genericError", { defaultValue: "Erro ao buscar histórico de vacinação." }));
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-background px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Search card — hidden on print */}
        <Card className="print:hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Syringe className="h-5 w-5 text-primary" />
              {t("vaccineVerify.title", { defaultValue: "Carteira de Vacinação" })}
            </CardTitle>
            <CardDescription>
              {t("vaccineVerify.description", { defaultValue: "Consulte o histórico de vacinação de um paciente." })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pet-id">{t("vaccineVerify.petIdLabel", { defaultValue: "ID do paciente" })}</Label>
              <Input
                id="pet-id"
                value={petId}
                onChange={(e) => setPetId(e.target.value)}
                placeholder={t("vaccineVerify.petIdPlaceholder", { defaultValue: "Cole o ID do paciente aqui" })}
              />
            </div>

            <Button onClick={handleVerify} disabled={result === "checking"} className="w-full">
              {result === "checking" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("vaccineVerify.checking", { defaultValue: "Buscando..." })}
                </>
              ) : (
                t("vaccineVerify.button", { defaultValue: "Ver histórico de vacinação" })
              )}
            </Button>

            {(result === "error" || result === "not_found") && errorMessage && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <ShieldX className="h-4 w-4 shrink-0" />
                {errorMessage}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {result === "found" && data && (
          <div className="space-y-4">
            {/* Print header — only shown on print */}
            <div className="hidden print:block text-center mb-4">
              <h1 className="text-xl font-bold">Carteira de Vacinação</h1>
              <p className="text-sm text-muted-foreground">
                {t("vaccineVerify.generatedAt", { defaultValue: "Gerado em" })}: {formatDateBR(data.generated_at)}
              </p>
            </div>

            {/* Pet info */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{data.pet_name}</CardTitle>
                  <Button variant="outline" size="sm" onClick={handlePrint} className="print:hidden">
                    <Printer className="h-4 w-4 mr-2" />
                    {t("vaccineVerify.print", { defaultValue: "Imprimir" })}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3 text-sm">
                  {data.species && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t("vaccineVerify.species", { defaultValue: "Espécie" })}</p>
                      <p className="font-medium">{data.species}</p>
                    </div>
                  )}
                  {data.breed && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t("vaccineVerify.breed", { defaultValue: "Raça" })}</p>
                      <p className="font-medium">{data.breed}</p>
                    </div>
                  )}
                  {data.pet_birth_date && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t("vaccineVerify.birthDate", { defaultValue: "Nascimento" })}</p>
                      <p className="font-medium">{formatDateBR(data.pet_birth_date)}</p>
                    </div>
                  )}
                  {data.owner_name && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t("vaccineVerify.owner", { defaultValue: "Tutor" })}</p>
                      <p className="font-medium">{data.owner_name}</p>
                    </div>
                  )}
                </div>

                <Separator className="my-3" />

                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">
                    {t("vaccineVerify.totalVaccines", { defaultValue: "{{count}} vacina(s) registrada(s)", count: String(data.total) })}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Vaccination list */}
            {data.vaccinations.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                  {t("vaccineVerify.empty", { defaultValue: "Nenhuma vacinação registrada para este paciente." })}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {data.vaccinations.map((vax) => (
                  <Card key={vax.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Syringe className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-semibold text-sm">
                            {vax.vaccine_name ?? t("vaccineVerify.unknownVaccine", { defaultValue: "Vacina" })}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {t("vaccineVerify.dose", { defaultValue: "Dose {{number}}", number: String(vax.dose_number) })}
                        </Badge>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>
                            {t("vaccineVerify.appliedOn", { defaultValue: "Aplicada em" })}: <span className="text-foreground font-medium">{formatDateBR(vax.application_date)}</span>
                          </span>
                        </div>
                        {vax.next_dose_date && !vax.next_dose_completed_at && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>
                              {t("vaccineVerify.nextDose", { defaultValue: "Próxima dose" })}: <span className="text-foreground font-medium">{formatDateBR(vax.next_dose_date)}</span>
                            </span>
                          </div>
                        )}
                        {vax.batch_number && (
                          <div className="text-muted-foreground">
                            {t("vaccineVerify.batch", { defaultValue: "Lote" })}: <span className="text-foreground">{vax.batch_number}</span>
                          </div>
                        )}
                        {vax.expiry_date && (
                          <div className="text-muted-foreground">
                            {t("vaccineVerify.expiry", { defaultValue: "Validade do lote" })}: <span className="text-foreground">{formatDateBR(vax.expiry_date)}</span>
                          </div>
                        )}
                        {vax.vet_name && (
                          <div className="text-muted-foreground">
                            {t("vaccineVerify.vet", { defaultValue: "Veterinário" })}: <span className="text-foreground">{vax.vet_name}</span>
                            {vax.vet_crmv && <span className="ml-1 text-xs">({vax.vet_crmv})</span>}
                          </div>
                        )}
                        {vax.clinic_name && (
                          <div className="text-muted-foreground">
                            {t("vaccineVerify.clinic", { defaultValue: "Clínica" })}: <span className="text-foreground">{vax.clinic_name}</span>
                          </div>
                        )}
                      </div>

                      {(vax.notes || vax.adverse_reaction) && (
                        <Separator className="my-2" />
                      )}
                      {vax.notes && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("vaccineVerify.notes", { defaultValue: "Obs" })}: {vax.notes}
                        </p>
                      )}
                      {vax.adverse_reaction && (
                        <p className="text-xs text-destructive mt-1">
                          {t("vaccineVerify.adverseReaction", { defaultValue: "Reação adversa" })}: {vax.adverse_reaction}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Footer */}
            <p className="text-xs text-center text-muted-foreground print:block hidden">
              {t("vaccineVerify.footer", { defaultValue: "Documento gerado pelo sistema de gestão veterinária." })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
